"use client";

import { useMutation, useLazyQuery } from "@apollo/client";
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  DatePickerV2,
  ImageUploaderV2,
  type ImageUploaderV2File,
  Input,
  LiquidPill,
  LiquidPillGroup,
  LiquidPillGroupVertical,
  PageEmptyStateV2,
  PageLoadingStateV2,
  Textarea,
} from "components/ui/v2";
import { ProfileResumeBuilderV2 } from "components/ui/v2/profile/resume";
import { gqlFeedback, gqlUser } from "gql";
import {
  Award,
  BarChart3,
  Brain,
  BriefcaseBusiness,
  Building2,
  Cake,
  Check,
  Coins,
  Github,
  Globe2,
  GraduationCap,
  HeartHandshake,
  Instagram,
  Languages as LanguagesIcon,
  Linkedin,
  Mail,
  Medal,
  Pencil,
  Sparkles,
  Trophy,
  UserRound,
} from "lucide-react";
import moment from "moment";
import setLanguage from "next-translate/setLanguage";
import useTranslation from "next-translate/useTranslation";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { USER_LANG } from "@constants/global";
import type { IUser } from "types/User.types";
import type { IVendor } from "types/Vendor.types";
import { getQueryOperator } from "utils/helpers";
import { isUrl } from "utils/string";
import { putToSignedUrl } from "utils/storage-upload";
import isEmail from "validator/lib/isEmail";

type ProfileMenu = "profile" | "progress" | "recognitions" | "exchanges" | "resume";
type ProfileTab = "personalData" | "evaluation" | "arcana";
type ResumeTab = "experiences" | "certifications" | "skills" | "fields" | "project";

const PROFILE_MENUS: Array<{ value: ProfileMenu; labelKey: string; icon: React.ReactNode; variant: any }> = [
  { value: "profile", labelKey: "Menu.profile", icon: React.createElement(UserRound, { className: "h-3.5 w-3.5" }), variant: "slate" },
  { value: "progress", labelKey: "Menu.progress", icon: React.createElement(Trophy, { className: "h-3.5 w-3.5" }), variant: "slate" },
  { value: "recognitions", labelKey: "Menu.recognitions", icon: React.createElement(HeartHandshake, { className: "h-3.5 w-3.5" }), variant: "slate" },
  { value: "exchanges", labelKey: "Menu.exchanges", icon: React.createElement(Coins, { className: "h-3.5 w-3.5" }), variant: "slate" },
  { value: "resume", labelKey: "Menu.resume", icon: React.createElement(BriefcaseBusiness, { className: "h-3.5 w-3.5" }), variant: "slate" },
];

const PROFILE_TABS: Array<{ value: ProfileTab; labelKey: string; variant: any }> = [
  { value: "personalData", labelKey: "Tabs.personalData", variant: "indigo" },
  { value: "evaluation", labelKey: "Tabs.evaluation", variant: "sky" },
  { value: "arcana", labelKey: "Tabs.arcana", variant: "violet" },
];

const RESUME_TABS: Array<{ value: ResumeTab; labelKey: string; variant: any; icon: React.ReactNode }> = [
  { value: "experiences", labelKey: "Tabs.experiences", variant: "indigo", icon: React.createElement(BriefcaseBusiness, { className: "h-3.5 w-3.5" }) },
  { value: "certifications", labelKey: "Tabs.certifications", variant: "emerald", icon: React.createElement(Award, { className: "h-3.5 w-3.5" }) },
  { value: "skills", labelKey: "Tabs.skills", variant: "violet", icon: React.createElement(Brain, { className: "h-3.5 w-3.5" }) },
  { value: "fields", labelKey: "Tabs.fields", variant: "sky", icon: React.createElement(Building2, { className: "h-3.5 w-3.5" }) },
  { value: "project", labelKey: "Tabs.project", variant: "gold", icon: React.createElement(GraduationCap, { className: "h-3.5 w-3.5" }) },
];

type ProfileFormValues = {
  name: string;
  lastname: string;
  email: string;
  username: string;
  shortBio: string;
  linkedin: string;
  instagram: string;
  github: string;
  birthday: Date | null;
};

function asString(value: unknown) {
  return typeof value === "string" ? value : "";
}

function clampPercent(value?: number | null) {
  return Math.max(0, Math.min(100, Number(value ?? 0)));
}

function NativeProgress({ value, label }: { value: number; label?: React.ReactNode }) {
  const safe = clampPercent(value);
  return (
    <div className="space-y-1.5">
      {label ? <div className="text-xs font-medium text-slate-500 dark:text-zinc-400">{label}</div> : null}
      <progress
        value={safe}
        max={100}
        className="h-2 w-full overflow-hidden rounded-full [&::-moz-progress-bar]:rounded-full [&::-moz-progress-bar]:bg-emerald-500 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-slate-100 dark:[&::-webkit-progress-bar]:bg-zinc-800 [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-emerald-500"
      />
    </div>
  );
}

function MetricCard({ icon, title, value, variant = "indigo" }: { icon: React.ReactNode; title: string; value: React.ReactNode; variant?: any }) {
  return (
    <Card size="full" padding="comfortable" shadow="subtle" entranceAnimation>
      <div className="flex items-center gap-3">
        <LiquidPill as="span" shape="circle" size="md" variant={variant} appearance="tinted" shadow="neutral" icon={icon} />
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-500 dark:text-zinc-400">{title}</p>
          <p className="truncate text-lg font-semibold text-slate-950 dark:text-white">{value}</p>
        </div>
      </div>
    </Card>
  );
}

async function uploadFileToSignedUrl(file: File, signedUrl: (args: any) => Promise<any>) {
  const { data } = await signedUrl({ variables: { key: `${Date.now()}-${file.name}` } });
  const url = data?.getSignedUrl;
  if (!url) return null;
  return putToSignedUrl(url, file).catch(() => null);
}

export interface ProfilePageContentV2Props {
  user: IUser;
  vendor: (IVendor & { isDevelopmentVendor?: boolean }) | null;
  activeMenu: ProfileMenu;
  activeProfileTab: ProfileTab;
  activeResumeTab: ResumeTab;
  visibleMenus: typeof PROFILE_MENUS;
  pushMenu: (nextMenu: ProfileMenu) => void;
  pushTab: (tab: ProfileTab | ResumeTab) => void;
  updateUserData: (data: any) => void;
  currentLang: string;
  recognitions: any[];
  exchanges: any[];
  coinsLoading: boolean;
  userLevelLoading: boolean;
  mySpaceLoading: boolean;
  userLevel: any;
  mySpace: any;
}

export function ProfilePageContentV2({
  user,
  vendor,
  activeMenu,
  activeProfileTab,
  activeResumeTab,
  visibleMenus,
  pushMenu,
  pushTab,
  updateUserData,
  currentLang,
  recognitions,
  exchanges,
  coinsLoading,
  userLevelLoading,
  mySpaceLoading,
  userLevel,
  mySpace,
}: ProfilePageContentV2Props) {
  const { t } = useTranslation("profile");

  return (
    <div className="grid gap-6 lg:grid-cols-[13.5rem_minmax(0,1fr)]">
          <aside className="space-y-3">
            <div className="hidden lg:block">
              <LiquidPillGroupVertical
                items={visibleMenus.map((item) => ({ value: item.value, label: t(item.labelKey), icon: item.icon, variant: item.variant }))}
                activeValue={activeMenu}
                onChange={(value) => pushMenu(value as ProfileMenu)}
                size="sm"
              />
            </div>
            <div className="overflow-x-auto pb-1 lg:hidden">
              <LiquidPillGroup
                items={visibleMenus.map((item) => ({ value: item.value, label: t(item.labelKey), icon: item.icon, variant: item.variant }))}
                activeValue={activeMenu}
                onChange={(value) => pushMenu(value as ProfileMenu)}
                size="sm"
              />
            </div>
          </aside>

          <main className="min-w-0 space-y-6">
            {activeMenu === "profile" ? (
              <>
                <LiquidPillGroup
                  items={PROFILE_TABS.map((item) => ({ value: item.value, label: t(item.labelKey), variant: item.variant }))}
                  activeValue={activeProfileTab}
                  onChange={(value) => pushTab(value as ProfileTab)}
                  size="sm"
                />
                {activeProfileTab === "personalData" ? <PersonalDataPanel user={user} updateUserData={updateUserData} currentLang={currentLang} /> : null}
                {activeProfileTab === "evaluation" ? <EvaluationPanel user={user} /> : null}
                {activeProfileTab === "arcana" ? <ArcanaPanel birthday={user.birthday} /> : null}
              </>
            ) : null}

            {activeMenu === "progress" ? (
              <ProgressPanelV2
                loading={userLevelLoading || mySpaceLoading}
                userLevel={userLevel}
                mySpace={mySpace}
              />
            ) : null}
            {activeMenu === "recognitions" ? <RecognitionsPanelV2 recognitions={recognitions} /> : null}
            {activeMenu === "exchanges" ? <ExchangesPanelV2 loading={coinsLoading} exchanges={exchanges} /> : null}
            {activeMenu === "resume" ? (
              <>
                <div className="w-fit rounded-full border border-slate-200/70 bg-white/90 p-1 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.18)] dark:border-zinc-800/70 dark:bg-zinc-950/80">
                  <LiquidPillGroup
                    items={RESUME_TABS.filter((item) => item.value !== "project" || vendor?.enableCorporateValue).map((item) => ({ value: item.value, label: t(item.labelKey), icon: item.icon, variant: item.variant }))}
                    activeValue={activeResumeTab}
                    onChange={(value) => pushTab(value as ResumeTab)}
                    size="sm"
                  />
                </div>
                <ResumePanelV2 activeTab={activeResumeTab} />
              </>
            ) : null}
          </main>
        </div>
  );
}

function PersonalDataPanel({ user, updateUserData, currentLang }: { user: any; updateUserData: (data: any) => void; currentLang: string }) {
  const { t } = useTranslation("common");
  const [avatarFile, setAvatarFile] = React.useState<ImageUploaderV2File | null>(null);
  const [avatarUploading, setAvatarUploading] = React.useState(false);
  const [newLang, setNewLang] = React.useState(user?.lang || currentLang);
  const [signedUrl] = useLazyQuery(gqlFeedback.queries.GENERATE_SIGNED_URL, { fetchPolicy: "network-only" });
  const [updateProfile, { loading }] = useMutation(gqlUser.mutations.UPDATE_USER_PROFILE, {
    awaitRefetchQueries: true,
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_FULL_USER), getQueryOperator(gqlUser.queries.USER_LEVEL)],
    onCompleted({ updateProfile }) {
      updateUserData(updateProfile);
    },
  });

  const { control, handleSubmit, formState: { errors } } = useForm<ProfileFormValues>({
    values: {
      name: user?.name || "",
      lastname: user?.lastname || "",
      email: user?.email || "",
      username: user?.username || "",
      shortBio: user?.shortBio || "",
      linkedin: user?.linkedin || "",
      instagram: user?.instagram || "",
      github: user?.github || "",
      birthday: user?.birthday ? new Date(user.birthday) : null,
    },
  });

  const onSubmit = async (data: ProfileFormValues) => {
    await toast.promise(
      updateProfile({ variables: { updateProfileInput: { ...data, lang: newLang, birthDate: data.birthday } } }),
      { loading: t("Actualizando perfil"), success: t("Perfil actualizado correctamente"), error: t("Ocurrió un error al actualizar el perfil") }
    );
    if (newLang !== currentLang) {
      setLanguage(newLang, false);
      localStorage.setItem(USER_LANG, newLang);
    }
  };

  const handleAvatarChange = async (file: ImageUploaderV2File | null) => {
    if (!file?.file) {
      setAvatarFile(file);
      return;
    }
    setAvatarFile(file);
    setAvatarUploading(true);
    try {
      const profileImage = await uploadFileToSignedUrl(file.file, signedUrl);
      if (!profileImage) {
        toast.error(t("Ocurrió un error al actualizar la foto de perfil"));
        setAvatarFile(null);
        return;
      }
      await toast.promise(
        updateProfile({ variables: { updateProfileInput: { profileImage } } }),
        {
          loading: t("Actualizando foto de perfil"),
          success: t("Foto de perfil actualizada correctamente"),
          error: t("Ocurrió un error al actualizar la foto de perfil"),
        }
      );
      setAvatarFile(null);
    } catch {
      setAvatarFile(null);
    } finally {
      setAvatarUploading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <Card size="full" padding="none" shadow="subtle" entranceAnimation>
        <div className="relative overflow-hidden rounded-[22px] bg-gradient-to-br from-indigo-50 via-white to-sky-50 p-4 dark:from-indigo-950/40 dark:via-zinc-900 dark:to-sky-950/30 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <div className="w-auto shrink-0">
                <ImageUploaderV2
                  value={avatarFile ?? (user?.profileImage ? { preview: user.profileImage, name: "profile", file: undefined as unknown as File } : null)}
                  onChange={handleAvatarChange}
                  disabled={avatarUploading}
                  size="lg"
                  label={t("Foto de perfil")}
                  changeLabel={t("Cambiar")}
                  emptyHint={t("Subir foto")}
                />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-xl font-semibold text-slate-950 dark:text-white">{[user?.name, user?.lastname].filter(Boolean).join(" ")}</h2>
                <p className="truncate text-sm text-slate-500 dark:text-zinc-400">{user?.email}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <LiquidPill variant="indigo" appearance="tinted" size="xs" icon={<Mail className="h-3 w-3" />} text={user?.email ? t("Email verificado") : t("Sin email")} />
                  <LiquidPill variant="emerald" appearance="tinted" size="xs" icon={<Check className="h-3 w-3" />} text={t("Perfil activo")} />
                </div>
              </div>
            </div>
            <Button type="submit" className="shrink-0" loading={loading} disabled={loading || avatarUploading} leftIcon={<Pencil className="h-4 w-4" />}>{t("Actualizar Datos")}</Button>
          </div>
        </div>
      </Card>

      <Card size="full" padding="comfortable" shadow="subtle">
        <CardHeader align="left" title={t("Datos personales")} description={t("Mantén tu información actualizada")} />
        <div className="grid gap-4 md:grid-cols-2">
          <Controller name="name" control={control} rules={{ required: t("Ingresa tu nombre") as string }} render={({ field }) => <Input label={t("Nombre")} error={errors.name?.message} {...field} />} />
          <Controller name="lastname" control={control} render={({ field }) => <Input label={t("Apellido")} {...field} />} />
          <Controller name="birthday" control={control} render={({ field }) => <DatePickerV2 label={t("Cumpleaños")} value={field.value} onChange={field.onChange} />} />
          <Controller name="email" control={control} rules={{ validate: (value) => isEmail(value) || t("Debes ingresar un e-mail válido") }} render={({ field }) => <Input label={t("Email")} disabled error={errors.email?.message} {...field} />} />
          <div className="md:col-span-2">
            <Controller name="shortBio" control={control} render={({ field }) => <Textarea label={t("Acerca de ti")} rows={4} maxLength={200} showCount {...field} />} />
          </div>
        </div>
      </Card>

      <Card size="full" padding="comfortable" shadow="subtle">
        <CardHeader align="left" title={t("Datos organizacionales")} description={t("Información conectada a tu organización")} />
        <div className="grid gap-4 md:grid-cols-2">
          <Controller name="username" control={control} render={({ field }) => <Input label={t("Nombre de usuario")} {...field} />} />
          <Input label={t("Nivel de inglés")} value={user?.englishLevel || t("Desconocido")} disabled readOnly leftIcon={<LanguagesIcon className="h-4 w-4" />} />
          <Input label={t("Fecha de Ingreso")} value={user?.activeSince ? moment(user.activeSince).format("DD/MM/YYYY") : "—"} disabled readOnly />
          <div>
            <p className="mb-2 text-xs font-medium text-slate-600 dark:text-zinc-400">{t("Idioma")}</p>
            <LiquidPillGroup
              items={[{ value: "es", label: "ES", variant: "indigo" }, { value: "en", label: "EN", variant: "sky" }]}
              activeValue={newLang}
              onChange={setNewLang}
              size="sm"
            />
          </div>
        </div>
      </Card>

      <Card size="full" padding="comfortable" shadow="subtle">
        <CardHeader align="left" title={t("Links externos")} description={t("Conecta tus perfiles profesionales")} />
        <div className="grid gap-4 md:grid-cols-3">
          <Controller name="linkedin" control={control} rules={{ validate: (value) => !value || isUrl(value) || t("Verifica que sea una url válida") }} render={({ field }) => <Input label="LinkedIn" leftIcon={<Linkedin className="h-4 w-4" />} error={errors.linkedin?.message} {...field} />} />
          <Controller name="github" control={control} rules={{ validate: (value) => !value || isUrl(value) || t("Verifica que sea una url válida") }} render={({ field }) => <Input label="Github" leftIcon={<Github className="h-4 w-4" />} error={errors.github?.message} {...field} />} />
          <Controller name="instagram" control={control} rules={{ validate: (value) => !value || isUrl(value) || t("Verifica que sea una url válida") }} render={({ field }) => <Input label="Instagram" leftIcon={<Instagram className="h-4 w-4" />} error={errors.instagram?.message} {...field} />} />
        </div>
      </Card>
    </form>
  );
}

function EvaluationPanel({ user }: { user: any }) {
  const { t } = useTranslation("common");
  const last = user?.lastAppreciation;
  if (!last) {
    return <PageEmptyStateV2 variant="empty" icon={<BarChart3 className="h-8 w-8" />} title={t("No has realizado ninguna evaluación")} description={t("Para poder medir tu nivel de madurez, pide a tu líder que te evalúe")} />;
  }
  const overall = clampPercent(last.userGeneralMaturityLevel);
  const maturity = overall >= 80 ? { text: "Senior", variant: "emerald" } : overall >= 60 ? { text: "Semi Senior", variant: "sky" } : { text: "Junior", variant: "gold" };
  return (
    <Card size="full" padding="comfortable" shadow="subtle" entranceAnimation>
      <CardHeader align="left" title={t("Última evaluación")} description={last.appreciation?.name} />
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <LiquidPill variant={maturity.variant as any} appearance="tinted" size="sm" icon={<Medal className="h-4 w-4" />} text={maturity.text} />
          <LiquidPill variant="slate" appearance="tinted" size="sm" text={moment(last.createdAt).format("DD/MM/YYYY")} />
        </div>
        <NativeProgress value={overall} label={`${t("Madurez General")} · ${overall.toFixed(1)}%`} />
        <div className="grid gap-3 md:grid-cols-2">
          {(last.userMaturityLevelByDimension ?? []).map((dimension: any) => (
            <Card key={dimension.dimensionId} size="full" padding="comfortable" shadow="subtle">
              <div className="flex items-center justify-between gap-3">
                <p className="line-clamp-1 text-sm font-semibold text-slate-800 dark:text-zinc-100">{dimension.dimensionName}</p>
                <LiquidPill variant={dimension.percent >= 80 ? "emerald" : dimension.percent >= 60 ? "sky" : "gold"} appearance="tinted" size="xs" text={dimension.maturityname} />
              </div>
              <NativeProgress value={dimension.percent} label={`${dimension.percent}%`} />
            </Card>
          ))}
        </div>
      </div>
    </Card>
  );
}

const ARCANA_CARDS = [
  { name: "El Loco", keyword: "inicio", message: "Apertura, curiosidad y valentía para comenzar de nuevo." },
  { name: "El Mago", keyword: "manifestación", message: "Tienes recursos internos para convertir intención en acción." },
  { name: "La Sacerdotisa", keyword: "intuición", message: "Escucha tu mundo interior antes de tomar una decisión." },
  { name: "La Emperatriz", keyword: "creatividad", message: "Tu energía crece cuando nutres ideas, vínculos y proyectos." },
  { name: "El Emperador", keyword: "estructura", message: "Orden, foco y liderazgo te ayudan a sostener avances reales." },
  { name: "El Hierofante", keyword: "aprendizaje", message: "La experiencia compartida y la guía abren nuevas perspectivas." },
  { name: "Los Enamorados", keyword: "elección", message: "Alinear valores y decisiones fortalece tus relaciones." },
  { name: "El Carro", keyword: "dirección", message: "La determinación bien enfocada te mueve hacia tus objetivos." },
  { name: "La Justicia", keyword: "equilibrio", message: "Claridad, responsabilidad y coherencia ordenan el camino." },
  { name: "El Ermitaño", keyword: "sabiduría", message: "La pausa consciente revela respuestas que estaban cerca." },
  { name: "La Rueda", keyword: "cambio", message: "Los ciclos se mueven: adapta tu energía al nuevo momento." },
  { name: "La Fuerza", keyword: "coraje", message: "La calma y la constancia son tu forma más alta de poder." },
  { name: "El Colgado", keyword: "perspectiva", message: "Mirar desde otro ángulo puede destrabar lo que parecía fijo." },
  { name: "La Muerte", keyword: "transformación", message: "Cerrar etapas libera espacio para una versión más auténtica." },
  { name: "La Templanza", keyword: "armonía", message: "La integración paciente produce cambios duraderos." },
  { name: "El Diablo", keyword: "liberación", message: "Reconocer patrones limitantes es el primer paso para soltarlos." },
  { name: "La Torre", keyword: "revelación", message: "Lo que cae muestra dónde construir con más verdad." },
  { name: "La Estrella", keyword: "esperanza", message: "Tu visión se renueva cuando confías en lo que estás cultivando." },
  { name: "La Luna", keyword: "sensibilidad", message: "No todo está claro aún: avanza escuchando señales sutiles." },
  { name: "El Sol", keyword: "vitalidad", message: "Claridad, alegría y energía disponible para compartir." },
  { name: "El Juicio", keyword: "despertar", message: "Una llamada interna te invita a responder con madurez." },
  { name: "El Mundo", keyword: "integración", message: "Cierre, logro y expansión: reconoce cuánto has avanzado." },
];

function ArcanaPanel({ birthday }: { birthday?: string | Date | null }) {
  const { t } = useTranslation("common");
  if (!birthday) {
    return <PageEmptyStateV2 variant="empty" icon={<Sparkles className="h-8 w-8" />} title={t("No has ingresado tu fecha de nacimiento")} description={t("Para poder calcular tu Arcana, debes ingresar tu fecha de nacimiento")} />;
  }

  const digits = moment(birthday).format("DDMMYYYY").split("").map(Number);
  const sum = digits.reduce((acc, digit) => acc + digit, 0);
  const number = ((sum - 1) % 22) + 1;
  const arcana = ARCANA_CARDS[number - 1];

  return (
    <Card size="full" padding="none" shadow="subtle" entranceAnimation>
      <section className="relative overflow-hidden p-5 sm:p-6">
        <div aria-hidden className="pointer-events-none absolute -right-20 -top-24 h-56 w-56 rounded-full bg-violet-400/20 blur-3xl dark:bg-violet-500/10" />
        <div aria-hidden className="pointer-events-none absolute -bottom-20 -left-16 h-52 w-52 rounded-full bg-indigo-400/15 blur-3xl dark:bg-indigo-500/10" />

        <div className="relative grid gap-6 lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-center">
          <div className="mx-auto w-full max-w-[17rem] rounded-[2rem] border border-violet-200/70 bg-gradient-to-br from-violet-50 via-white to-indigo-50 p-4 shadow-[0_18px_60px_-32px_rgba(124,58,237,0.55)] dark:border-violet-900/50 dark:from-violet-950/40 dark:via-zinc-900 dark:to-indigo-950/30">
            <div className="flex aspect-[3/4] flex-col items-center justify-between rounded-[1.5rem] border border-white/70 bg-white/70 p-5 text-center shadow-inner dark:border-white/10 dark:bg-zinc-950/55">
              <LiquidPill
                as="span"
                shape="circle"
                size="2xl"
                variant="violet"
                appearance="tinted"
                surfaceStyle="selectionSolid"
                shadow="default"
              >
                <span className="text-2xl font-bold text-white">{number}</span>
              </LiquidPill>

              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-violet-500 dark:text-violet-300">
                  Arcana personal
                </p>
                <h3 className="text-2xl font-bold text-slate-950 dark:text-white">
                  {arcana.name}
                </h3>
              </div>

              <LiquidPill
                as="span"
                variant="indigo"
                appearance="tinted"
                size="xs"
                shadow="neutral"
                text={arcana.keyword}
              />
            </div>
          </div>

          <div className="space-y-4 text-center lg:text-left">
            <div className="space-y-2">
              <LiquidPill
                as="span"
                variant="violet"
                appearance="tinted"
                size="sm"
                shadow="neutral"
                icon={<Sparkles className="h-4 w-4" />}
                text={t("Tu Arcana")}
              />
              <h2 className="text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
                {arcana.name}
              </h2>
              <p className="max-w-2xl text-sm leading-6 text-slate-600 dark:text-zinc-300">
                {arcana.message}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <MetricCard
                icon={<Cake className="h-4 w-4" />}
                title={t("Fecha de nacimiento")}
                value={moment(birthday).format("DD/MM/YYYY")}
                variant="rose"
              />
              <MetricCard
                icon={<Sparkles className="h-4 w-4" />}
                title="Arcana"
                value={`#${number}`}
                variant="violet"
              />
              <MetricCard
                icon={<Globe2 className="h-4 w-4" />}
                title={t("Energía")}
                value={arcana.keyword}
                variant="indigo"
              />
            </div>
          </div>
        </div>
      </section>
    </Card>
  );
}

function ProgressPanelV2({ loading, userLevel, mySpace }: { loading: boolean; userLevel: any; mySpace: any }) {
  const { t } = useTranslation("common");
  if (loading) return <PageLoadingStateV2 title={t("Cargando")} description={t("Tu progreso en Groowcity")} />;
  const goal = mySpace?.goal;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard icon={<Check className="h-4 w-4" />} title={t("Mi Nivel")} value={userLevel?.level ?? 0} variant="emerald" />
        <MetricCard icon={<GraduationCap className="h-4 w-4" />} title={t("Experiencia")} value={`${userLevel?.experience ?? 0}/${userLevel?.nextLevelExp ?? 0}`} variant="sky" />
        <MetricCard icon={<HeartHandshake className="h-4 w-4" />} title={t("Comentarios")} value={userLevel?.comments ?? 0} variant="gold" />
      </div>
      <Card size="full" padding="comfortable" shadow="subtle">
        <CardHeader align="left" title={t("Metas y tareas")} description={goal ? goal.name : t("No existe ninguna meta asociada")} />
        {goal ? (
          <div className="space-y-4">
            <NativeProgress value={goal.completionPercentage ?? 0} label={`${goal.completionPercentage ?? 0}%`} />
            <div className="grid gap-3">
              {(goal.tasks ?? []).map((task: any) => (
                <Card key={task.id} size="full" padding="comfortable" shadow="subtle">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-slate-900 dark:text-white">{task.name}</p>
                    <LiquidPill variant={task.completed ? "emerald" : "slate"} appearance="tinted" size="xs" text={task.completed ? t("Completada") : t("Pendiente")} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <PageEmptyStateV2 variant="empty" title={t("No existe ninguna meta asociada")} description={t("Habla con tu líder para que te asigne metas y tareas")} />
        )}
      </Card>
    </div>
  );
}

function RecognitionsPanelV2({ recognitions }: { recognitions: any[] }) {
  const { t } = useTranslation("common");
  if (!recognitions.length) return <PageEmptyStateV2 variant="empty" icon={<HeartHandshake className="h-8 w-8" />} title={t("Aún no tienes reconocimientos")} description={t("Los reconocimientos recibidos y enviados aparecerán aquí")} />;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {recognitions.map((recognition) => (
        <Card key={recognition.corporateValueId} size="full" padding="comfortable" shadow="subtle" entranceAnimation>
          <div className="flex items-center gap-3">
            <Avatar src={recognition.avatar || undefined} name={recognition.corporateValueTitle} size="md" />
            <div className="min-w-0 flex-1">
              <h3 className="truncate font-semibold text-slate-950 dark:text-white">{recognition.corporateValueTitle}</h3>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <LiquidPill variant="emerald" appearance="tinted" size="xs" text={`${recognition.quantityReceived ?? 0} ${t("recibidos")}`} />
                <LiquidPill variant="sky" appearance="tinted" size="xs" text={`${recognition.quantitySend ?? 0} ${t("enviados")}`} />
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ExchangesPanelV2({ loading, exchanges }: { loading: boolean; exchanges: any[] }) {
  const { t } = useTranslation("common");
  if (loading) return <PageLoadingStateV2 title={t("Cargando")} description={t("Mis canjes")} />;
  if (!exchanges.length) return <PageEmptyStateV2 variant="empty" icon={<Coins className="h-8 w-8" />} title={t("Aún no canjeas productos en el marketplace")} description={t("Revisa los productos que puedes canjear con tus monedas")} />;
  return (
    <div className="grid gap-3">
      {exchanges.map((exchange, index) => (
        <Card key={exchange.id ?? index} size="full" padding="comfortable" shadow="subtle">
          <div className="flex items-center gap-3">
            <Avatar src={exchange.productImage || undefined} name={exchange.productName} size="md" />
            <div className="min-w-0 flex-1">
              <h3 className="truncate font-semibold text-slate-950 dark:text-white">{exchange.productName}</h3>
              <p className="line-clamp-2 text-sm text-slate-500 dark:text-zinc-400">{exchange.productDescription}</p>
            </div>
            <LiquidPill variant="gold" appearance="tinted" size="xs" text={`x${exchange.productQuantity ?? 1}`} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function ResumePanelV2({ activeTab }: { activeTab: ResumeTab }) {
  return <ProfileResumeBuilderV2 activeTab={activeTab} />;
}
